import { Link, StaticQuery, graphql } from "gatsby";
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import Icon from "./icon";
import { cn } from "../lib/helpers";
import Container from "./container";
import { useCookies } from 'react-cookie';
import PortableText from "./portableText";
import { Helmet } from 'react-helmet';
import Svg from 'react-inlinesvg';
import NewsItem from "./news-item";
import { useLocation } from '@reach/router';
import { elementScrollIntoViewPolyfill } from "seamless-scroll-polyfill";

import * as styles from "../styles/components/header.module.css";
import CookierBanner from "./cookier-banner";
import AnnouncementBar from "./announcement-bar";

const query = graphql`
  fragment SanityImage on SanityMainImage {
    crop {
      _key
      _type
      top
      bottom
      left
      right
    }
    hotspot {
      _key
      _type
      x
      y
      height
      width
    }
    asset {
      _id
    }
  }

  query HeaderQuery {
    site: sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
      _rawHeaderNotification
      announcementBar {
        id
        isVisible
        _rawContent
        callToAction {
          blank
          secondaryButton
          text
          url
          document {
            asset {
              originalFilename
              url
            }
          }
        }
      }
    }

    mainMenu: sanityMenu(title: { eq: "Main Menu" }) {
      menuItems {
        _key
        text
        url
        blank
        description
        megaMenuId
        subMenu {
          _key
          text
          url
          blank
          description
          icon {
            asset {
              url
            }
          }
        }
        hiddenOnDesktop
      }
    }

    utilMenu: sanityMenu(title: { eq: "Utility Menu" }) {
      menuItems {
        _key
        text
        url
        blank
        icon {
          asset {
            url
          }
        }
      }
    }

    mobileMenuCta: sanityMenu(title: { eq: "Mobile Menu Call To Actions" }) {
      menuItems {
        _key
        text
        url
        blank
        icon {
          asset {
            url
          }
        }
      }
    }

    megaMenus: sanityMenusMega {
      product {
        _rawHeading
        _rawHeadingExtended
        _rawContent
        _rawContentExtended
        menu {
          _key
          blank
          text
          url
          description
          icon {
            asset {
              url
            }
          }
        }
        menuExtendedReality {
          _key
          blank
          text
          url
          description
          icon {
            asset {
              url
            }
          }
        }
        menuReality {
          _key
          blank
          text
          url
          description
          icon {
            asset {
              url
            }
          }
        }
      }
      resources {
        title
        menu {
          _key
          blank
          text
          url
          description
          icon {
            asset {
              url
            }
          }
        }
      }
      showcase {
        menus {
          _key
          menu {
            _key
            blank
            text
            url
            icon {
              asset {
                url
              }
            }
          }
          title
        }
        link {
          blank
          text
          url
        }
      }
    }

    news: allSanityNews(
      sort: { fields: [publicationDate], order: DESC }
      limit: 1
    ) {
      edges {
        node {
          id
          title
          author {
            name
          }
          publicationDate
          slug {
            current
          }
          thumbnail {
            ...SanityImage
            alt
          }
        }
      }
    }
  }
`;

const Header = (props) => {

  const [showMenu, setShowMenu] = useState(false);
  const [hideNfBar, setHideNfBar] = useState(true);
  const [showSmHeader, setShowSmHeader] = useState(false);
  const [randomLogo, setRandomLogo] = useState(null);
  const [cookies, setCookie] = useCookies(['hideNotificationBar']);
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [isCookiePopupAsked,setIsCookiePopupAsked] = useState(false)

  // Header element/height
  const headerRef = useRef(null);
  const menuRef = useRef(null);
  const nbRef = useRef(null);
  let nbHeight;

  const { pathname, hash } = useLocation();

  useEffect(() => {
    const cookiePopupAsked = localStorage.getItem('marketoCookie');
    setIsCookiePopupAsked(cookiePopupAsked)
  }, [])
  
  
  // Shuffle logo versions
  function handleRandomLogo() {
    let logos = ['0', '1', '2'];
    if (randomLogo) {
      logos = logos.filter(item => item !== randomLogo);
    }
    setRandomLogo(logos[Math.floor(Math.random()*logos.length)]);
  }

  // Smooth scroll
  function smoothScroll(hash) {
    const id = hash.replace('#', '');
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function handleShowMenu() {
    setShowMenu(true);

    if(nbRef.current) {
      nbHeight = nbRef.current.offsetHeight;
      menuRef.current.style.height = 'calc(100% - ' + nbHeight + 'px)';
    }
    else {
      menuRef.current.style.height = '';
    }
  }

  function handleHideMenu() {
    if(showMenu) {
      setShowMenu(false);
    }
    if(menuRef.current) {
      menuRef.current.style.height = '';
    }
    const parents = document.getElementsByClassName('menu-has-children');
    for (const parent of parents) {
      parent.classList.remove('active');
    }
    document.body.classList.remove('overlay-active');
  }

  function handleToggleSubMenu(e) {
    e.preventDefault();
    const filter = document.getElementById('filter');
    if(filter) {
      filter.classList.remove('active', 'on-top');
    }

    if(e.target.parentElement.classList.contains('active')) {
      e.target.parentElement.classList.remove('active');
      document.body.classList.remove('overlay-active');
    }
    else {
      const parents = document.getElementsByClassName('menu-has-children');
      for (const parent of parents) {
        parent.classList.remove('active');
      }
      e.target.parentElement.classList.add('active');
      document.body.classList.add('overlay-active');
    }
  }

  function handleHideNotificationBar() {
    setCookie('hideNotificationBar', true, {maxAge: 604800});
    setHideNfBar(true);
  }

  function handleSetHideNfBar() {
    if(cookies.hideNotificationBar === undefined) {
      setHideNfBar(false);
    }
  }

  function updateMenuHeight() {
    const mobile = window.matchMedia(`(max-width: 959px)`).matches;

    if(mobile) {
      if(nbRef.current) {
        nbHeight = nbRef.current.offsetHeight;
        menuRef.current.style.top = nbHeight + 'px';
        if(showMenu) {
          menuRef.current.style.height = 'calc(100% - ' + nbHeight + 'px)';
        }
      }
      else {
        menuRef.current.style.top = '';
        menuRef.current.style.height = '';
      }
    }
    else {
      menuRef.current.style.top = '';
      menuRef.current.style.height = '';
    }
  }

  function updateHeroHeight() {
    const elem = document.getElementById("hero");

    if(elem) {
      if(nbRef.current) {
        nbHeight = nbRef.current.offsetHeight;
        elem.style.minHeight = 'calc(100vh - ' + nbHeight + 'px)';
      }
      else {
        elem.style.minHeight = '';
      }
    }
  }

  function updateBodyPadding() {
    const elem = document.body;

    if(elem) {
      if(nbRef.current) {
        nbHeight = nbRef.current.offsetHeight;
        elem.style.paddingTop = nbHeight + 'px';
      }
      else {
        elem.style.paddingTop = '';
      }
    }
  }

  function handleScroll() {
    const isScrolled = window.pageYOffset > 100;
    
    if (isScrolled !== showSmHeader) {
      setShowSmHeader(!showSmHeader);
    }
  }

  useEffect(() => {
    elementScrollIntoViewPolyfill();

    document.querySelectorAll('#header a:not(.mega-menu-toggle)').forEach(link => {
      link.addEventListener('click', e => {
        const eHash = e.target.hash;
        const eTarget = e.target.target;
        const ePathname = e.target.pathname;

        if(eTarget === '_blank') {
          setTimeout(()=>{
            handleHideMenu();
          }, 100); // Fix the mega menu being visible when you go back to the tab
        }
        else {
          if(ePathname === pathname) {
            e.preventDefault();
            if(eHash !== '') {
              smoothScroll(eHash);
            }
            handleHideMenu();
          }
          else {
            if(pathname === '/' && ePathname === undefined) {
              handleHideMenu();
            }
            else {
              setTimeout(()=>{
                handleHideMenu();
              }, 600);
            }
          }
        }
      });
    });

    // if a hash link, scroll to top, then scroll to id
    if (hash !== '') {
      setTimeout(() => {
        window.scrollTo(0, 0);
      }, 50);

      setTimeout(() => {
        smoothScroll(hash);
      }, 600);
    }
  }, [pathname, hash]); // do this on route change



  useLayoutEffect(() => {
    handleSetHideNfBar();
    handleScroll();

    let checkExist = setInterval(function() {
      if (headerRef.current) {
        updateMenuHeight();
        updateHeroHeight();
        updateBodyPadding();
        clearInterval(checkExist);
      }
    }, 100); // check every 100ms

    window.addEventListener('resize', updateMenuHeight);
    window.addEventListener('resize', updateHeroHeight);
    window.addEventListener('resize', updateBodyPadding);
    document.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', updateMenuHeight);
      window.removeEventListener('resize', updateHeroHeight);
      window.removeEventListener('resize', updateBodyPadding);
      document.removeEventListener('scroll', handleScroll);
    }
  });

  return (
  <StaticQuery
    query={query}
    render={data => (
      <header className={styles.root} data-scroll={showSmHeader ? undefined : 'top'} ref={headerRef} id="header">
          <Helmet>
            <body className={showMenu ? 'menu-open' : undefined} />
          </Helmet>
          { showCookieBanner && isCookiePopupAsked !== "true" && <CookierBanner setShowCookieBanner={setShowCookieBanner} /> }
          {data?.site?.announcementBar?._rawContent && data?.site?.announcementBar?.isVisible &&  <AnnouncementBar button={data?.site?.announcementBar?.callToAction} id={data?.site?.announcementBar?.id} _rawContent={data?.site?.announcementBar?._rawContent} />}
          <div className={cn(styles.main, "color-palette-border-light")}>
            <div className={cn("grid justify-lg-between", styles.grid)}>
              <div className={cn("col-lg-auto", styles.colLogo)}>

                <div className={cn(styles.branding, "color-palette-border-light", randomLogo && "hover-"+randomLogo)} onMouseEnter={handleRandomLogo}>
                  <Link to="/">
                    <span className={styles.big}><Icon symbol="logo" /></span>
                    <span className={styles.small}><Icon symbol="logo-small" /></span>
                  </Link>
                </div>

                <div className={styles.menuWrap} ref={menuRef}>

                  {data.mainMenu && data.mainMenu.menuItems && data.mainMenu.menuItems.length > 0 && (
                    <nav className={styles.mainMenuBlock}>
                      <ul className={styles.mainMenu}>
                        {data.mainMenu.menuItems.map(menuItem => (
                          <>
                          {menuItem.megaMenuId ? (
                            <li className={cn("menu-has-children color-palette-pseudo-bg-dark color-palette-border-light", menuItem.hiddenOnDesktop && styles.dHide)} key={menuItem._key}>

                              <Link className="mega-menu-toggle color-palette-pseudo-bg-dark color-palette-svg-stroke" to={menuItem.url} onClick={handleToggleSubMenu}>
                                <div className={cn(styles.text, "color-palette-color")}>{menuItem.text}</div>
                                <div className={styles.description}>{menuItem.description}</div>
                                <Icon symbol="chevron-down" />
                              </Link>

                              {data.megaMenus && (
                                <div className={cn(styles.megaMenu, "color-palette-bg-dark")}>
                                  <Container megaMenuContainer>
                                    {menuItem.megaMenuId === 'product' && (

                                      <div className={cn(styles.product)}>
                                        <div style={{display:'flex', flexDirection:'column'}} className={cn(styles.featuredProductBorder,'color-palette-color','color-palette-pseudo-border')}>

                                          <div className={cn("")}>
                                            <div className={cn("entry-content cw-355", styles.content)}>
                                              {data.megaMenus.product._rawHeading && (
                                                <div className={styles.contentHeading}><PortableText blocks={data.megaMenus.product._rawHeading} /></div>
                                              )}
                                              {data.megaMenus.product._rawContent && (
                                                <PortableText blocks={data.megaMenus.product._rawContent} /> 
                                              )}
                                            </div>
                                          </div>

                                          <div className="">
                                            {data.megaMenus.product.menuReality && data.megaMenus.product.menuReality.length > 0 && (
                                              <ul className={styles.subMenuCard}>
                                                {data.megaMenus.product.menuReality.map(menuItem => (
                                                  <li className={styles.item} key={menuItem._key}>
                                                    {menuItem.blank ? (
                                                      <a href={menuItem.url} target="_blank" rel="noreferrer" className={cn("color-palette-color color-palette-pseudo-border ",!menuItem?.icon?.asset && styles.noBorder)}>
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={cn(styles.text, "color-palette-svg-stroke")}>{menuItem.text}<Icon symbol="external" /></div>
                                                          <div className={cn(styles.description,!menuItem?.icon?.asset && styles.productDescription)}>{menuItem.description}</div>
                                                        </div>
                                                      </a>
                                                    ) : (
                                                      <Link to={menuItem.url} className={cn("color-palette-color color-palette-pseudo-border",!menuItem?.icon?.asset && styles.noBorder)}>
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={styles.text}>{menuItem.text}</div>
                                                          <div className={cn(styles.description,!menuItem?.icon?.asset &&  styles.productDescription)}>{menuItem.description}</div>
                                                        </div>
                                                      </Link>
                                                    )}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                          </div>

                                        </div>
                                        <div style={{display:'flex', flexDirection:'column'}} className={ cn(styles.featuredProductBorder,'color-palette-color','color-palette-pseudo-border')}>

                                          <div className={cn("")}>
                                            <div className={cn("entry-content cw-355", styles.content)}>
                                              {data.megaMenus.product._rawHeading && (
                                                <div className={styles.contentHeading}><PortableText blocks={data.megaMenus.product._rawHeadingExtended} /></div>
                                              )}
                                              {data.megaMenus.product._rawContentExtended && (
                                                <PortableText blocks={data.megaMenus.product._rawContentExtended} /> 
                                              )}
                                            </div>
                                          </div>

                                          <div className="">
                                            {data.megaMenus.product.menuExtendedReality && data.megaMenus.product.menuExtendedReality.length > 0 && (
                                              <ul className={styles.subMenuCard}>
                                                {data.megaMenus.product.menuExtendedReality.map(menuItem => (
                                                  <li className={styles.item} key={menuItem._key}>
                                                    {menuItem.blank ? (
                                                      <a href={menuItem.url} target="_blank" rel="noreferrer" className={cn("color-palette-color color-palette-pseudo-border ",!menuItem?.icon?.asset && styles.noBorder)}>
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={cn(styles.text, "color-palette-svg-stroke")}>{menuItem.text}<Icon symbol="external" /></div>
                                                          <div className={cn(styles.description,!menuItem?.icon?.asset && styles.productDescription)}>{menuItem.description}</div>
                                                        </div>
                                                      </a>
                                                    ) : (
                                                      <Link to={menuItem.url} className={cn("color-palette-color color-palette-pseudo-border",!menuItem?.icon?.asset && styles.noBorder)}>
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={styles.text}>{menuItem.text}</div>
                                                          <div className={cn(styles.description,!menuItem?.icon?.asset && styles.productDescription)}>{menuItem.description}</div>
                                                        </div>
                                                      </Link>
                                                    )}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                          </div>

                                        </div>
                                      </div>

                                    )}

                                    {menuItem.megaMenuId === 'showcase' && (
                                      
                                      <div className={styles.showcase}>

                                        {data.megaMenus.showcase.menus && data.megaMenus.showcase.menus.length > 0 && (
                                          <div className="grid">
                                            {data.megaMenus.showcase.menus.map((menu, i) => (
                                              <div className={cn("col-12 col-lg-4", i===0 ? styles.colMenuCard : styles.colMenu)} key={menu._key}>
                                                <div className={cn("h7", styles.sectionTitle)}>{menu.title}</div>

                                                  {menu.menu && menu.menu.length > 0 && (
                                                    <ul className={i===0 ? cn(styles.subMenuCard, styles.lite) : styles.subMenu}>
                                                      {menu.menu.map(menuItem => (
                                                        <li key={menuItem._key} className={styles.item}>
                                                          {menuItem.blank ? (
                                                            <a href={menuItem.url} target="_blank" rel="noreferrer" className="color-palette-color color-palette-pseudo-border">
                                                              {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                              <div className={styles.textWrap}>
                                                                <div className={cn(styles.text, "color-palette-svg-stroke", i!==0 && "color-palette-underline")}>{menuItem.text}<Icon symbol="external" /></div>
                                                              </div>
                                                            </a>
                                                          ) : (
                                                            <Link to={menuItem.url} className="color-palette-color color-palette-pseudo-border">
                                                              {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                              <div className={styles.textWrap}>
                                                                <div className={cn(styles.text, i!==0 && "color-palette-underline")}>{menuItem.text}</div>
                                                              </div>
                                                            </Link>
                                                          )}
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  )}

                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {data.megaMenus.showcase.link && data.megaMenus.showcase.link.url && (
                                          <>
                                            {data.megaMenus.showcase.link.blank ? (
                                              <a href={data.megaMenus.showcase.link.url} target="_blank" rel="noreferrer" className={cn("link color-palette-color-underline color-palette-svg-stroke", styles.link)}>{data.megaMenus.showcase.link.text}<Icon symbol="arrow-right" /></a>
                                            ) : (
                                              <Link to={data.megaMenus.showcase.link.url} className={cn("link color-palette-color-underline color-palette-svg-stroke", styles.link)}>{data.megaMenus.showcase.link.text}<Icon symbol="arrow-right" /></Link>
                                            )}
                                          </>
                                        )}

                                      </div>

                                    )}


                                    {menuItem.megaMenuId === 'resources' && (
                                      
                                      <div className={styles.resources}>
                                        <div className="grid">

                                          <div className="col-12 col-lg-8">
                                            <div className={cn("h7", styles.sectionTitle)}>{data.megaMenus.resources.title}</div>

                                            {data.megaMenus.resources.menu && data.megaMenus.resources.menu.length > 0 && (
                                              <ul className={styles.subMenuCard}>
                                                {data.megaMenus.resources.menu.map(menuItem => (
                                                  <li className={styles.item} key={menuItem._key}>
                                                    {menuItem.blank ? (
                                                      <a href={menuItem.url} target="_blank" rel="noreferrer" className="color-palette-color color-palette-pseudo-border">
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={cn(styles.text, "color-palette-svg-stroke")}>{menuItem.text}<Icon symbol="external" /></div>
                                                          <div className={styles.description}>{menuItem.description}</div>
                                                        </div>
                                                      </a>
                                                    ) : (
                                                      <Link to={menuItem.url} className="color-palette-color color-palette-pseudo-border">
                                                        {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                                        <div className={styles.textWrap}>
                                                          <div className={styles.text}>{menuItem.text}</div>
                                                          <div className={styles.description}>{menuItem.description}</div>
                                                        </div>
                                                      </Link>
                                                    )}
                                                  </li>
                                                ))}
                                              </ul>
                                            )}
                                          </div>

                                          <div className={cn("col-4", styles.mHide)}>
                                            {data.news && (
                                              <div className={styles.news}>
                                                <div className={cn("h7", styles.sectionTitle)}>Latest</div>
                                                <Link to="/news-updates" className={cn("link color-palette-color-underline color-palette-svg-stroke", styles.link)}>View All<Icon symbol="arrow-right" /></Link>
                                                {data.news.edges.map(({node: news}) => (
                                                  <div key={news.id} className={styles.item}>
                                                    <NewsItem news={news} />
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>

                                        </div>
                                      </div>

                                    )}
                                  </Container>
                                </div>
                              )}

                            </li>

                          ) : (

                            <li key={menuItem._key} className={cn(menuItem.subMenu && menuItem.subMenu.length > 0 && "menu-has-children", "color-palette-pseudo-bg-dark color-palette-border-light", menuItem.hiddenOnDesktop && styles.dHide)}>
                              {menuItem.blank ? (
                                <a className={cn(menuItem.subMenu && menuItem.subMenu.length > 0 && "mega-menu-toggle", "color-palette-pseudo-bg-dark color-palette-svg-stroke")} href={menuItem.url} target="_blank" rel="noreferrer" onClick={menuItem.subMenu && menuItem.subMenu.length > 0 ? handleToggleSubMenu : undefined}>
                                  <div className={cn(styles.text, "color-palette-color")}>{menuItem.text}</div>
                                  <div className={styles.description}>{menuItem.description}</div>
                                  {menuItem.subMenu && menuItem.subMenu.length > 0 && (<Icon symbol="chevron-down" />)}
                                </a>
                              ) : (
                                <Link className={cn(menuItem.subMenu && menuItem.subMenu.length > 0 && "mega-menu-toggle", "color-palette-pseudo-bg-dark color-palette-svg-stroke")} to={menuItem.url} onClick={menuItem.subMenu && menuItem.subMenu.length > 0 ? handleToggleSubMenu : undefined}>
                                  <div className={cn(styles.text, "color-palette-color")}>{menuItem.text}</div>
                                  <div className={styles.description}>{menuItem.description}</div>
                                  {menuItem.subMenu && menuItem.subMenu.length > 0 && (<Icon symbol="chevron-down" />)}
                                </Link>
                              )}

                              {menuItem.subMenu && menuItem.subMenu.length > 0 && (
                                <div className={cn(styles.megaMenu, "color-palette-bg-dark")}>
                                  <Container>
                                    <ul className={cn(styles.subMenuCard, styles.lite)}>
                                      {menuItem.subMenu.map(menuItem => (
                                        <li key={menuItem._key} className={styles.item}>
                                          {menuItem.blank ? (
                                            <a href={menuItem.url} target="_blank" rel="noreferrer" className="color-palette-color color-palette-pseudo-border">
                                              {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                              <div className={styles.textWrap}>
                                                <div className={cn(styles.text, "color-palette-svg-stroke")}>{menuItem.text}<Icon symbol="external" /></div>
                                              </div>
                                            </a>
                                          ) : (
                                            <Link to={menuItem.url} className="color-palette-color color-palette-pseudo-border">
                                              {menuItem.icon && menuItem.icon.asset && (<div className={cn(styles.icon, "color-palette-pseudo-border color-palette-svg-fill")}><Svg src={menuItem.icon.asset.url} uniquifyIDs={true} /></div>)}
                                              <div className={styles.textWrap}>
                                                <div className={styles.text}>{menuItem.text}</div>
                                              </div>
                                            </Link>
                                          )}
                                        </li>
                                      ))}
                                    </ul>
                                  </Container>
                                </div>
                              )}
                            </li>

                          )}
                          </>
                        ))}
                      </ul>
                    </nav>

                  )}

                  {data.mobileMenuCta && data.mobileMenuCta.menuItems && data.mobileMenuCta.menuItems.length > 0 && (
                    <div className={cn(styles.mobileMenuCta, styles.dHide, "color-palette-border-light")}>
                      <ul>
                        {data.mobileMenuCta.menuItems.slice(0, 2).map(menuItem => (
                          <li className={cn(styles.item, "color-palette-border-light")} key={menuItem._key}>
                            {menuItem.blank ? (
                              <a href={menuItem.url} target="_blank" rel="noreferrer" className="color-palette-color color-palette-svg-stroke">
                                {menuItem.text}{menuItem.icon && menuItem.icon.asset ? (<Svg src={menuItem.icon.asset.url} uniquifyIDs={true} />) : (<Icon symbol="external" />)}
                              </a>
                            ) : (
                              <Link to={menuItem.url} className="color-palette-color">
                                {menuItem.text}{menuItem.icon && menuItem.icon.asset && <Svg src={menuItem.icon.asset.url} uniquifyIDs={true} />}
                              </Link>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                </div>
              </div>

              <div className={cn("col-lg-auto", styles.colUtil)}>
                <nav>
                  <ul className={cn(styles.utilMenu, "color-palette-border-light")}>
                    {data.utilMenu && data.utilMenu.menuItems && data.utilMenu.menuItems.length > 0 && (
                      <>
                        {data.utilMenu.menuItems.slice(0, 3).map((menuItem, i) => (
                          <li key={menuItem._key} className="color-palette-border-light">
                            {menuItem.blank ? (
                              <a href={menuItem.url} target="_blank" rel="noreferrer" className={i===0 ? "color-palette-bg" : "color-palette-color color-palette-svg-stroke color-palette-pseudo-bg-dark"}>
                                <span className={i!==0 ? "color-palette-underline" : undefined}>{menuItem.text}</span>
                                {menuItem.icon && menuItem.icon.asset && (<Svg src={menuItem.icon.asset.url} uniquifyIDs={true} />)}
                              </a>
                            ) : (
                              <Link to={menuItem.url} className={i===0 ? "color-palette-bg" : "color-palette-color color-palette-svg-stroke color-palette-pseudo-bg-dark"}>
                                <span className={i!==0 ? "color-palette-underline" : undefined}>{menuItem.text}</span>
                                {menuItem.icon && menuItem.icon.asset && (<Svg src={menuItem.icon.asset.url} uniquifyIDs={true} />)}
                              </Link>
                            )}
                          </li>
                        ))}
                      </>
                    )}
                    <li>
                      <button
                        className={cn(styles.menuToggle, "color-palette-color")}
                        onClick={showMenu ? handleHideMenu : handleShowMenu}
                      >
                        <span>{showMenu ? "Close" : "Menu"}</span>
                        <span className={cn(styles.icon, "color-palette-pseudo-bg")}></span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>


            </div>
          </div>

          {data.site._rawHeaderNotification && !hideNfBar && (
            <div className={cn(styles.notificationBar, "color-palette-bg-dark")} ref={nbRef} id="notification-bar">
              <Container>
                <div className="entry-content"><PortableText blocks={data.site._rawHeaderNotification} /></div>
              </Container>
              <button onClick={handleHideNotificationBar}>
                <span className="screen-reader-text">Close</span>
                <Icon symbol="close" />
              </button>
            </div>
          )}
      
      </header>
    )}
  />
)};

export default Header;
